import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Copy } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { owner } from "@/lib/owner-api";

export default function OwnerSecurity() {
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try { setStatus(await owner.tfaStatus()); } catch (err) { console.warn("TFA status load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchStatus(); }, []);

  const startSetup = async () => {
    try { setSetupData(await owner.tfaSetup()); toast.success("Scan QR di authenticator app"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const enable = async () => {
    if (code.length !== 6) { toast.error("Kode 6 digit"); return; }
    try {
      await owner.tfaEnable(code);
      toast.success("2FA berhasil diaktifkan!");
      setSetupData(null); setCode(""); fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Kode salah"); }
  };

  const disable = async () => {
    if (code.length !== 6) { toast.error("Kode 6 digit"); return; }
    try {
      await owner.tfaDisable(code);
      toast.success("2FA dinonaktifkan");
      setCode(""); fetchStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Kode salah"); }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Security</h1>
        <p className="text-sm text-gray-500 mt-1">Two-factor authentication untuk akun Super Owner.</p>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className={`w-11 h-11 rounded-lg border flex items-center justify-center ${status?.enabled ? "bg-emerald-900/30 border-emerald-800 text-emerald-400" : "bg-gray-800 border-gray-700 text-gray-400"}`}>
            <ShieldCheck />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-semibold text-white">Two-Factor Authentication (TOTP)</h3>
              {status?.enabled ? <Badge className="bg-emerald-900/30 text-emerald-400 border border-emerald-800">Aktif</Badge>
                : <Badge className="bg-gray-800 text-gray-500 border border-gray-700">Non-aktif</Badge>}
            </div>
            <p className="mt-1 text-sm text-gray-400">Gunakan Google Authenticator, Authy, atau app authenticator lain untuk generate kode 6-digit setiap login.</p>
          </div>
        </div>

        {!status?.enabled && !setupData && (
          <Button onClick={startSetup} className="mt-6 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="setup-2fa-btn">
            Aktifkan 2FA
          </Button>
        )}

        {setupData && (
          <div className="mt-6 border-t border-gray-800 pt-6 space-y-4">
            <div className="flex flex-col items-center bg-white rounded-lg p-6">
              <QRCodeCanvas value={setupData.provisioning_uri} size={200} level="M" />
            </div>
            <div className="text-xs text-gray-400 text-center">Scan QR dengan Google Authenticator / Authy</div>
            <div className="bg-gray-800 border border-gray-700 rounded p-3 text-center">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Manual key (kalau tidak bisa scan)</div>
              <div className="font-mono text-sm text-white break-all">{setupData.secret}</div>
              <button onClick={() => { navigator.clipboard.writeText(setupData.secret); toast.success("Copied"); }} className="mt-2 text-xs text-emerald-400 inline-flex items-center gap-1"><Copy size={12} /> Salin</button>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-gray-500">Verify: Masukkan kode 6-digit</label>
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="mt-1 bg-gray-800 border-gray-700 text-white text-center font-mono text-lg tracking-widest" placeholder="000000" data-testid="tfa-code-input" />
            </div>
            <Button onClick={enable} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="enable-2fa-btn">Konfirmasi & Aktifkan</Button>
          </div>
        )}

        {status?.enabled && (
          <div className="mt-6 border-t border-gray-800 pt-6 space-y-3">
            <div className="text-sm text-gray-400">Untuk nonaktifkan 2FA, masukkan kode dari authenticator app Anda:</div>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} className="bg-gray-800 border-gray-700 text-white text-center font-mono text-lg tracking-widest" placeholder="000000" data-testid="disable-tfa-code" />
            <Button onClick={disable} variant="outline" className="w-full border-red-900 bg-red-950/30 text-red-400 hover:bg-red-900/50" data-testid="disable-2fa-btn">
              Nonaktifkan 2FA
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
